// Me — one profile screen for everyone (docs/ONE_APP.md).
//
// There were two, one per phone shell, and they had drifted: Field's carried
// GPS, Job help and the install offer; Dispatch's carried the theme toggle
// first and a different set of links. A person's own details are not a
// property of which shell they happened to be in, so there is one.
//
// The "Elsewhere" block is GONE, and its absence is the point. It used to
// offer "Open Dispatch", "Open Office" and "Switch to the classic app" —
// three links that only made sense because there were four applications. There
// is one now, so there is nowhere else to go.

import React, { useState } from 'react';
import { Pill, FwButton, SectionLabel } from '../../ui/fw';
import { ThemeToggle } from '../../ui';
import { useAuth, useSettings } from '../../App';
import { useRole } from '../../hooks/usePermission';
import APP_VERSION from '../../version';
import ChangePassword from '../shared/ChangePassword';
import MyDetails from '../shared/MyDetails';
import WhatsNew from '../shared/WhatsNew';
import InstallApp from '../shared/InstallApp';
import JobHelp from '../shared/JobHelp';
import ReportApp from '../shared/ReportApp';
import { roleLabel } from '../shared/roles';
import { gpsEnabled, setGpsEnabled } from '../field/geo';
import { gpsRequired } from '../field/gpsGate';
import { forceUpdate } from './UpdateGate';
import './Me.css';

// The role vocabulary is apps/shared/roles.js. This file kept a fourth copy of
// the same table; it happened to agree, which is exactly why it would have
// been the one to diverge unnoticed at the next rename.

export default function Me() {
  const { user, logout } = useAuth();
  const role = useRole();
  const [pwOpen, setPwOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  /**
   * Handing one change line from What's new to Report a problem (#53).
   *
   * `reportSeed` is the text; `reportSeq` is what REMOUNTS ReportApp, and only
   * when a seed is supplied. Opening Report a problem the ordinary way keeps
   * the same key, so a half-typed report still survives being dismissed and
   * reopened — ReportApp has no reset-on-open and that is deliberate (its
   * sessionRef comment says why). A remount is the only way to seed its
   * initial state without adding an effect that would race that discipline.
   */
  const [reportSeed, setReportSeed] = useState('');
  const [reportSeq, setReportSeq] = useState(0);
  const reportOnChange = (item) => {
    setNewsOpen(false);
    setReportSeed(`Something looks wrong with this change: "${item}"\n\n`);
    setReportSeq((n) => n + 1);
    setReportOpen(true);
  };
  // On unless the person turns it off, and the switch is here rather than
  // buried in a settings screen a crew member never opens.
  const [gps, setGps] = useState(() => gpsEnabled());
  const [updating, setUpdating] = useState(false);
  // ── When the company requires a location, this switch is not a choice ─────
  //
  // actions.js clockIn asks the phone for a fix regardless of this preference
  // when `gps_required` is on, deliberately: somebody who turned it off months
  // ago must not meet a new company policy as a clock-in that will not start.
  // So the switch reads ON, cannot be moved, and SAYS WHY. A control showing
  // "Off" beside a clock-in that takes a fix every time would be the screen
  // lying about itself, and the reason nobody could find would be here.
  const { settings } = useSettings();
  const gpsLocked = gpsRequired(settings);
  const gpsOn = gpsLocked || gps;

  return (
    <div className="fw-me">
      <section className="fw-me__card">
        <div className="fw-me__who">
          <div className="fw-me__name">{(user && user.name) || ''}</div>
          <Pill tone="sched">{roleLabel(role)}</Pill>
        </div>
        <div className="fw-me__row">
          <span className="fw-me__label">Theme</span>
          <ThemeToggle />
        </div>
        <div className="fw-me__row">
          <span className="fw-me__label">
            Record location
            {gpsLocked ? <span className="fw-me__note">Your company requires it to start the clock</span> : null}
          </span>
          <label className="fw-me__switch">
            <input
              type="checkbox"
              checked={gpsOn}
              disabled={gpsLocked}
              onChange={(e) => { setGps(e.target.checked); setGpsEnabled(e.target.checked); }}
              aria-label="Record location when clocking in and out"
            />
            <span>{gpsOn ? 'On' : 'Off'}</span>
          </label>
        </div>
        <div className="fw-me__row">
          <span className="fw-me__label">Version</span>
          <span className="fw-num">{APP_VERSION}</span>
        </div>
      </section>

      <section className="fw-me__card">
          <SectionLabel>Account</SectionLabel>
        <div className="fw-me__stack">
          <FwButton variant="ghost" size="lg" full icon="profile" onClick={() => setDetailsOpen(true)}>My details</FwButton>
          <FwButton variant="ghost" size="lg" full icon="lock" onClick={() => setPwOpen(true)}>Change password</FwButton>
          <FwButton variant="ghost" size="lg" full icon="info" onClick={() => setNewsOpen(true)}>What&apos;s new</FwButton>
        </div>
      </section>

      <section className="fw-me__card">
          <SectionLabel>Help</SectionLabel>
        <div className="fw-me__stack">
          {/* The second way in. The job screen is the first — that is where it
              is used — but somebody between jobs still has a question. */}
          <FwButton variant="ghost" size="lg" full icon="search" onClick={() => setHelpOpen(true)}>Job help</FwButton>
          {/* "The app is wrong", as opposed to "the JOB is blocked", which is
              Report a problem on the tab bar. The classic control for this
              lived in the desktop sidebar — inside an aside a phone pushes off
              screen — and was reported as missing, accurately, for months. */}
          <FwButton variant="ghost" size="lg" full icon="alertTriangle" onClick={() => setReportOpen(true)}>
            Report a problem with the app
          </FwButton>
          {/* Not dismissible here: you opened Me on purpose, and this is the
              way back for anybody who dismissed the banner on Today. */}
          <InstallApp />
          {/* The manual escape hatch. UpdateGate offers the update on its own
              when the service worker reports one, and this is what a crew is
              told to press when the app is behaving oddly — so it has to work
              on the machine where the automatic path already did not. It
              clears every cached build and unregisters the worker; it does not
              touch localStorage, so nobody loses their theme or their
              customised bar to it (noLocalStorageClear.test.js). */}
          <FwButton
            variant="ghost"
            size="lg"
            full
            icon="refresh"
            className="fw-me__update"
            loading={updating}
            onClick={() => { setUpdating(true); forceUpdate(); }}
          >
            Reload the app
          </FwButton>
        </div>
      </section>

      <FwButton variant="ghost" size="lg" full icon="logout" onClick={logout}>Log out</FwButton>

      <ChangePassword open={pwOpen} onClose={() => setPwOpen(false)} />
      <MyDetails open={detailsOpen} user={user} onClose={() => setDetailsOpen(false)} />
      <WhatsNew open={newsOpen} onClose={() => setNewsOpen(false)} onReport={reportOnChange} />
      {/* `online` is read here rather than passed: JobHelp's links all leave
          the app, and a search button with no signal opens a tab onto an error. */}
      <ReportApp key={reportSeq} prefill={reportSeed} open={reportOpen} onClose={() => setReportOpen(false)} />
      <JobHelp open={helpOpen} onClose={() => setHelpOpen(false)} online={typeof navigator === 'undefined' ? true : navigator.onLine !== false} />
    </div>
  );
}
